import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  ArrowLeft,
  LogOut,
  Ticket,
  Calendar,
  MapPin,
  Sparkles,
  Send,
  Home,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { beneApi, formatApiError, BENE_TOKEN_KEY } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const TOKEN_KEY = BENE_TOKEN_KEY;

const inputCls =
  "w-full rounded-2xl border border-pblv-ink/15 bg-white px-5 py-3.5 text-base text-pblv-ink outline-none transition-colors focus:border-pblv-mauve";

const STATUS_LABEL = {
  nouveau: { label: "En cours de traitement", tone: "bg-pblv-rose/25 text-[#8a5a1e]" },
  validée: { label: "Validée", tone: "bg-emerald-100 text-emerald-700" },
  validee: { label: "Validée", tone: "bg-emerald-100 text-emerald-700" },
  utilisée: { label: "Utilisée", tone: "bg-pblv-sage/20 text-[#5f7052]" },
  traité: { label: "Traité", tone: "bg-pblv-sage/20 text-[#5f7052]" },
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await beneApi.post("/beneficiaires/request-login", { email });
      setStep("code");
      setCode("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossible d'envoyer le code");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.replace(/\D/g, "").length !== 6) {
      toast.error("Entrez les 6 chiffres du code reçu par e-mail.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await beneApi.post("/beneficiaires/verify", { email, code });
      toast.success("Connexion réussie");
      onLogin(data.token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Code invalide");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col justify-center px-5 py-16 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-3xl border border-pblv-ink/10 bg-white p-8 shadow-[0_12px_40px_rgba(45,40,48,0.06)] md:p-10"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pblv-mauve">
          Espace bénéficiaire
        </p>
        <h1 className="mt-3 font-serif text-3xl font-medium leading-tight text-pblv-ink md:text-4xl">
          Bienvenue chez vous.
        </h1>

        {step === "code" ? (
          <>
            <div
              data-testid="espace-login-sent"
              className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
            >
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" strokeWidth={2} />
              <div>
                <p className="font-semibold text-emerald-900">Si votre adresse est enregistrée, vous allez recevoir un code.</p>
                <p className="mt-1 text-sm text-emerald-800">
                  Code à 6 chiffres, valable 10 minutes. Pensez à vérifier vos spams.
                </p>
              </div>
            </div>
            <form onSubmit={verifyCode} className="mt-6 flex flex-col gap-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-pblv-ink">Code reçu par e-mail</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  data-testid="espace-login-code"
                  className={`${inputCls} text-center font-serif text-2xl tracking-[0.5em]`}
                />
              </div>
              <button
                type="submit"
                disabled={verifying}
                data-testid="espace-login-verify"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-60"
              >
                {verifying ? "Vérification…" : "Me connecter"}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => setStep("email")} data-testid="espace-login-change-email" className="text-pblv-inksoft underline underline-offset-4 hover:text-pblv-mauve">
                  Changer d'adresse
                </button>
                <button type="button" onClick={requestCode} disabled={sending} data-testid="espace-login-resend" className="font-semibold text-pblv-mauve underline underline-offset-4 disabled:opacity-60">
                  {sending ? "Envoi…" : "Renvoyer un code"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="mt-3 text-base leading-relaxed text-pblv-inksoft">
              Entrez votre adresse e-mail — nous vous envoyons un code de connexion à usage unique,
              valable 10 minutes. Pas de mot de passe à retenir.
            </p>
            <form onSubmit={requestCode} className="mt-8 flex flex-col gap-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-pblv-ink">
                  Votre e-mail
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-pblv-mauve" strokeWidth={1.8} />
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre.email@exemple.com"
                    data-testid="espace-login-email"
                    className={`${inputCls} pl-12`}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="espace-login-submit"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-60"
              >
                {sending ? "Envoi…" : "Recevoir mon code de connexion"}
                <Send className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </form>
          </>
        )}
      </motion.div>

      <p className="mt-6 text-center text-sm text-pblv-inksoft">
        Pas encore de demande T'POZ ?{" "}
        <Link
          to="/#demande-bon"
          data-testid="espace-login-to-demande"
          className="font-semibold text-pblv-mauve underline decoration-pblv-rose underline-offset-4"
        >
          Faire une première demande
        </Link>
      </p>
    </div>
  );
}

function ReserverDialog({ open, onOpenChange, onDone }) {
  const [lieux, setLieux] = useState([]);
  const [lieuId, setLieuId] = useState("");
  const [dates, setDates] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    beneApi
      .get("/lieux")
      .then(({ data }) => {
        setLieux(data);
        if (data[0]) setLieuId(data[0].id);
      })
      .catch(() => setLieux([]));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await beneApi.post("/beneficiaires/reservation", {
        lieu_id: lieuId,
        dates_souhaitees: dates,
        message,
      });
      toast.success("Réservation envoyée", {
        description: "L'équipe T'POZ revient vers vous rapidement.",
      });
      onOpenChange(false);
      setDates("");
      setMessage("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="espace-reserver-dialog"
        className="max-h-[90vh] overflow-y-auto rounded-3xl bg-pblv-bg sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium text-pblv-ink">
            Réserver une T'POZ
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Choisir un lieu</label>
            <select
              required
              value={lieuId}
              onChange={(e) => setLieuId(e.target.value)}
              data-testid="espace-reserver-lieu"
              className={inputCls}
            >
              {lieux.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} — {l.commune}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Dates souhaitées</label>
            <input
              type="text"
              placeholder="ex. 12-14 octobre, ou « à définir »"
              value={dates}
              onChange={(e) => setDates(e.target.value)}
              data-testid="espace-reserver-dates"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Message (optionnel)</label>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="espace-reserver-message"
              className={inputCls}
              placeholder="Quelques mots sur ce que vous cherchez pour cette T'POZ…"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !lieuId}
            data-testid="espace-reserver-submit"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-60"
          >
            {saving ? "Envoi…" : "Envoyer la demande de réservation"}
            <Send className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QuotaCard({ label, value, tint }) {
  return (
    <div className="rounded-2xl border border-pblv-ink/10 bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-pblv-inksoft">{label}</p>
      <p className="mt-2 font-serif text-4xl font-semibold" style={{ color: tint }}>{value}</p>
    </div>
  );
}

function Dashboard({ me, onLogout, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const q = me.quota;
  const firstname = useMemo(() => {
    if (me.demandes?.[0]?.nom) return me.demandes[0].nom.split(" ")[0];
    return me.email.split("@")[0];
  }, [me]);

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-10 md:px-8 md:py-16">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
          Espace bénéficiaire
        </p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-serif text-4xl font-medium leading-tight text-pblv-ink md:text-5xl">
            Bonjour, <span className="italic">{firstname}</span>.
          </h1>
          <button
            onClick={onLogout}
            data-testid="espace-logout"
            className="inline-flex items-center gap-2 rounded-full border border-pblv-ink/15 bg-white px-4 py-2 text-sm font-semibold text-pblv-ink transition-colors hover:bg-pblv-bg2"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </motion.div>

      {/* Quota */}
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <QuotaCard label="Restantes cette année" value={q.remaining} tint="#B98524" />
        <QuotaCard label="Demandes en cours" value={q.en_cours} tint="#9C809B" />
        <QuotaCard label="Validées à venir" value={q.upcoming} tint="#8FA07C" />
        <QuotaCard label="Utilisées" value={q.used} tint="#6b6470" />
      </div>

      <div className="mt-8 flex flex-col gap-4 rounded-3xl border border-pblv-mauve/20 bg-pblv-mauve/5 p-6 sm:flex-row sm:items-center sm:justify-between md:p-8">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> Nouvelle T'POZ
          </p>
          <h3 className="mt-2 font-serif text-2xl font-medium text-pblv-ink md:text-3xl">
            Réserver une T'POZ en 2 clics.
          </h3>
          <p className="mt-1 text-sm text-pblv-inksoft">
            {q.remaining > 0
              ? `Il vous reste ${q.remaining} T'POZ à activer cette année.`
              : "Vous avez utilisé vos 3 T'POZ de l'année — bravo !"}
          </p>
        </div>
        <button
          onClick={() => setDialogOpen(true)}
          disabled={q.remaining <= 0}
          data-testid="espace-reserver-cta"
          className="inline-flex items-center gap-2 rounded-full bg-pblv-mauve px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-50"
        >
          <Ticket className="h-4 w-4" strokeWidth={1.8} /> Réserver maintenant
        </button>
      </div>

      {/* History */}
      <div className="mt-10">
        <h2 className="font-serif text-2xl font-medium text-pblv-ink md:text-3xl">
          Historique de mes T'POZ
        </h2>
        <div className="mt-6 space-y-4" data-testid="espace-historique">
          {me.demandes.length === 0 && (
            <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-pblv-inksoft">
              Aucune demande enregistrée pour l'instant. Réservez votre première T'POZ ci-dessus.
            </p>
          )}
          {me.demandes.map((d) => {
            const st = STATUS_LABEL[d.status] || { label: d.status, tone: "bg-pblv-ink/10 text-pblv-ink" };
            return (
              <motion.article
                key={d.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                data-testid={`espace-demande-${d.id}`}
                className="rounded-2xl border border-pblv-ink/10 bg-white p-5 md:p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 font-serif text-xl font-medium text-pblv-ink">
                      {d.lieu ? (
                        <>
                          <Home className="h-4 w-4 text-pblv-mauve" strokeWidth={1.8} />
                          {d.lieu.name}
                        </>
                      ) : (
                        <>
                          <Ticket className="h-4 w-4 text-pblv-mauve" strokeWidth={1.8} />
                          Demande de Bon T'POZ
                        </>
                      )}
                    </p>
                    {d.lieu && (
                      <p className="mt-1 flex items-center gap-1 text-sm text-pblv-inksoft">
                        <MapPin className="h-3.5 w-3.5" /> {d.lieu.commune}
                      </p>
                    )}
                  </div>
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${st.tone}`}>
                    {st.label}
                  </span>
                </div>
                {d.message && (
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-pblv-inksoft">
                    {d.message}
                  </p>
                )}
                <p className="mt-3 flex items-center gap-1.5 text-xs text-pblv-inksoft">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(d.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                  <span className="mx-2">·</span>
                  Réf. {d.id.slice(0, 8)}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>

      <ReserverDialog open={dialogOpen} onOpenChange={setDialogOpen} onDone={onRefresh} />
    </div>
  );
}

export default function EspaceBeneficiaire() {
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = (msg) => {
    localStorage.removeItem(TOKEN_KEY);
    setTokenState(null);
    setMe(null);
    if (msg) toast.info(msg);
    else toast.success("À bientôt");
  };

  // Déconnexion automatique après 30 minutes d'inactivité
  useEffect(() => {
    if (!token || !me) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout("Session expirée après 30 minutes d'inactivité."), 30 * 60 * 1000);
    };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      evts.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, me]);

  const loadMe = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await beneApi.get("/beneficiaires/me");
      setMe(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setTokenState(null);
      } else {
        toast.error("Chargement impossible");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-pblv-bg2" data-testid="page-espace">
      <header className="sticky top-0 z-40 border-b border-pblv-ink/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-5 py-4 md:px-8">
          <Link
            to="/"
            data-testid="espace-back-home"
            className="inline-flex items-center gap-2 text-sm font-semibold text-pblv-ink transition-colors hover:text-pblv-mauve"
          >
            <ArrowLeft className="h-4 w-4" /> Retour au site
          </Link>
          <p className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve sm:block">
            Espace bénéficiaire T'POZ
          </p>
        </div>
      </header>

      {loading && token ? (
        <div className="flex min-h-[60vh] items-center justify-center text-pblv-inksoft">
          Chargement…
        </div>
      ) : token && me ? (
        <Dashboard me={me} onLogout={() => logout()} onRefresh={loadMe} />
      ) : (
        <LoginScreen
          onLogin={(t) => {
            localStorage.setItem(TOKEN_KEY, t);
            setTokenState(t);
          }}
        />
      )}
    </div>
  );
}
